import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/client/lib/api';
import { track } from '@/client/lib/analytics';
import { Turnstile, TURNSTILE_ENABLED } from '@/components/Turnstile';
import { useI18n } from '@/client/i18n/I18nContext';
import { lp } from '@/client/i18n/locale';

/**
 * «Avísame cuando vuelva» on a sold-out product page (docs/plan-agotado-aviso.md,
 * fase 3). A copy of the Newsletter form: consent with the privacy link, an
 * optional «La carta de CARO» box, honeypot `company` and Turnstile. The Worker
 * answers the same whatever happens (no oracle), so the success message only
 * says to check the inbox: nothing counts until the double opt-in click.
 */
export function RestockAlert({ productSlug }: { productSlug: string }) {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [alsoNewsletter, setAlsoNewsletter] = useState(false);
  const [company, setCompany] = useState(''); // honeypot
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!consent) {
      setStatus('error');
      return;
    }
    if (TURNSTILE_ENABLED && !tsToken) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      await api.restockAlert({
        email,
        productSlug,
        locale,
        consent,
        alsoNewsletter,
        company,
        turnstileToken: tsToken,
      });
      track('restock_signup');
      setStatus('done');
      setEmail('');
    } catch {
      setStatus('error');
      setTsReset((n) => n + 1);
    }
  }

  if (status === 'done') {
    return (
      <div className="caro-note" role="status">
        <p className="caro-note__body">{t('restock.done')}</p>
      </div>
    );
  }

  return (
    <form className="restock" onSubmit={onSubmit} noValidate aria-labelledby="restock-title">
      <p id="restock-title" className="eyebrow">{t('restock.title')}</p>
      <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('restock.body')}</p>
      <div className="restock__row">
        <label htmlFor="restock-email" className="visually-hidden">
          {t('common.email')}
        </label>
        <input
          id="restock-email"
          type="email"
          required
          autoComplete="email"
          aria-invalid={status === 'error' ? true : undefined}
          aria-describedby={status === 'error' ? 'restock-error' : undefined}
          placeholder={t('newsletter.placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" className="btn" disabled={status === 'loading'}>
          {status === 'loading' ? t('common.sending') : t('restock.submit')}
        </button>
      </div>
      <div className="honeypot" aria-hidden>
        <label>
          {t('newsletter.honeypot')}
          <input
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </label>
      </div>
      <label className="restock__check">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>
          {t('restock.consent')}{' '}
          {/* Router Link: the absolute path resolves under the locale basename (/en, /fr). */}
          <Link
            to={lp('/legal/privacidad')}
            className="link-underline"
            aria-label={t('newsletterPage.privacyLink')}
          >
            →
          </Link>
        </span>
      </label>
      <label className="restock__check">
        <input
          type="checkbox"
          checked={alsoNewsletter}
          onChange={(e) => setAlsoNewsletter(e.target.checked)}
        />
        <span>{t('restock.alsoLetter')}</span>
      </label>
      {TURNSTILE_ENABLED ? <Turnstile onVerify={setTsToken} resetSignal={tsReset} /> : null}
      {status === 'error' ? (
        <p id="restock-error" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--fs-sm)' }}>
          {t('restock.error')}
        </p>
      ) : null}
    </form>
  );
}
